// Matches — triage view.
//
// Two tabs, two data sources (intentional — they answer different questions):
//
//   Pending tab     → /api/matches/pending    (reads data/scan-history.tsv)
//   Evaluated tab   → /api/matches            (parses reports/*.md headers)
//
// The Pending count is NOT the same as the "new offers added" line printed by
// scan.mjs. The scan number counts every row appended to scan-history.tsv;
// the Pending list further applies the user's country filter
// (profile.yml `job_preferences.country_indeed`), the source/time/keyword
// chips, and excludes URLs already enqueued in /api/jobs. So a scan that
// reports "16 added" can legitimately show only e.g. 6 new rows here.
//
// Evaluation runs in the background via useJobRunnerGroup + /api/jobs.
// State survives navigation / refresh via localStorage.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import MatchRow from '../components/domain/MatchRow';
import Chip from '../components/ui/Chip';
import PendingJobsTable, { type PendingFilters } from '../components/domain/PendingJobsTable';
import EvaluationBanner from '../components/domain/EvaluationBanner';
import LocalEvalStatusBanner from '../components/domain/LocalEvalStatusBanner';
import { apiGet } from '../lib/api';
import { useZh } from '../lib/useProduct';
import { useJobRunnerGroup } from '../lib/useJobRunnerGroup';
import { fitTierLabels, REAL_FIT_TIERS, ALL_TIERS } from '../lib/fitTiers';
import { queryKeys } from '../lib/queryKeys';
import { useDeleteMatch } from '../lib/useDeleteMatch';
import type { Match, EvalTier, MatchesResponse, MatchTierCounts, PendingResponse, PendingRow, UserProfile } from '../lib/types';

type Tab = 'pending' | 'evaluated';

const PAGE_SIZE = 50;

// FIT_TIER_LABELS, REAL_FIT_TIERS, ALL_TIERS and effectiveTier() now live in
// ../lib/fitTiers (shared with other consumers); MatchesResponse + MatchTierCounts
// in ../lib/types. The Evaluated tab is now server-paginated + server-searched,
// so tier counts come from the server response (not computed client-side).

const POSTED_CHOICES: Array<{ label: string; hours: number | null }> = [
  { label: 'All', hours: null },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 24 * 7 },
  { label: '30d', hours: 24 * 30 },
];

const SOURCE_CHOICES = [
  { key: '', label: 'All' },
  { key: 'api', label: 'API' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'indeed', label: 'Indeed' },
  { key: 'glassdoor', label: 'Glassdoor' },
];

// Flip selected_for_cv on matching rows across BOTH cache shapes (the
// evaluated infinite query { pages:[{items}] } and any flat { items } cache).
function flipSelectedInCache(old: unknown, match: (id: string) => boolean, on: boolean): unknown {
  if (!old || typeof old !== 'object') return old;
  const o = old as Record<string, unknown>;
  const flip = (items: Match[]) => items.map((m) => (match(m.id) ? { ...m, selected_for_cv: on } : m));
  if (Array.isArray(o.pages)) {
    return { ...o, pages: (o.pages as Array<{ items?: Match[] }>).map((p) => ({ ...p, items: flip(p.items || []) })) };
  }
  if (Array.isArray(o.items)) return { ...o, items: flip(o.items as Match[]) };
  return old;
}

export default function Matches() {
  const zh = useZh();
  const nav = useNavigate();
  const qc = useQueryClient();
  // Tab state lives in the URL (?tab=pending|evaluated) so when the user
  // clicks into a match detail and hits Back, the browser restores the
  // evaluated tab instead of dropping them on Pending.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const tab: Tab = tabParam === 'evaluated' ? 'evaluated' : 'pending';
  const setTab = (next: Tab) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'pending') sp.delete('tab');
    else sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };
  const group = useJobRunnerGroup();
  // "Add by JD link" input (paste a posting URL → queue an evaluation).
  const [addUrl, setAddUrl] = useState('');

  // Read profile to pick up the country filter — applied to BOTH pending
  // (scanner rows) and evaluation queue, so API portal jobs and external
  // sources respect the same country constraint.
  const profileQ = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
    staleTime: 60_000,
  });
  const country = profileQ.data?.job_preferences?.country_indeed || '';
  // Free-form scan search term — used as a lightweight title-keyword
  // post-filter (any-token match). Pulls already-scraped rows whose
  // title contains NONE of the user's keywords out of the Pending list,
  // even if they were saved to scan-history.tsv before the filter
  // shipped. Empty string → server passes everything through.
  const searchTerm = profileQ.data?.job_preferences?.search_term || '';

  // URLs currently in the evaluation pipeline (any non-terminal status)
  const runningUrls = useMemo(() => {
    const s = new Set<string>();
    for (const e of group.entries) {
      if (e.status === 'queued' || e.status === 'pending' || e.status === 'running') s.add(e.url);
    }
    return s;
  }, [group.entries]);

  // ── Filter state, all URL-backed ──────────────────────────────────────
  // Filters live in the URL alongside `tab=` so navigating into /job/{id}
  // and hitting Back restores them. Each filter has a small helper that
  // (a) reads the current URL value with a sensible default, and (b)
  // writes back via setSearchParams using { replace: true } to avoid
  // spamming the browser history with one entry per keystroke.
  //
  // Default values are encoded as "missing key" so the URL stays clean
  // when nothing is filtered. Reading code calls .get(name) and falls
  // back to the default; writing code .delete()s when set to default,
  // .set()s otherwise.
  const setUrlParam = (name: string, value: string | null) => {
    const sp = new URLSearchParams(searchParams);
    if (value === null || value === '') sp.delete(name);
    else sp.set(name, value);
    setSearchParams(sp, { replace: true });
  };

  // Pending filters
  const pendingSource = searchParams.get('source') || '';
  const setPendingSource = (v: string) => setUrlParam('source', v || null);

  const pendingHoursStr = searchParams.get('hours');
  const pendingHours: number | null = pendingHoursStr ? parseInt(pendingHoursStr, 10) || null : null;
  const setPendingHours = (v: number | null) => setUrlParam('hours', v == null ? null : String(v));

  const pendingQ = searchParams.get('q') || '';
  const setPendingQ = (v: string) => setUrlParam('q', v || null);

  const pendingWithSalary = searchParams.get('salary') === '1';
  const setPendingWithSalary = (v: boolean) => setUrlParam('salary', v ? '1' : null);

  // Sort selector — URL-backed via ?sort=. Each tab has its own valid
  // option set; switching tabs preserves the param so a user that prefers
  // "salary high → low" gets it on both tabs without re-picking. The
  // backend ignores values it doesn't recognise (falls back to default).
  // Defined ABOVE pendingFilters because pendingFilters consumes pendingSort.
  const sortParam = searchParams.get('sort') || '';
  const setSort = (v: string) => setUrlParam('sort', v || null);
  const evaluatedSort = sortParam || 'score';
  const pendingSort = sortParam || 'first_seen';

  // Memoize so the child's useEffect(..., [filters]) doesn't fire on every
  // parent render — otherwise the child's selected-rows state gets wiped
  // the moment any unrelated state updates above this line.
  const pendingFilters = useMemo<PendingFilters>(() => ({
    sources: pendingSource ? [pendingSource] : [],
    postedWithinHours: pendingHours,
    q: pendingQ,
    withSalary: pendingWithSalary,
    country,
    keywords: searchTerm || undefined,
    sort: pendingSort,
  }), [pendingSource, pendingHours, pendingQ, pendingWithSalary, country, searchTerm, pendingSort]);

  // Evaluated filter — multi-select tier chips covering 5 tiers:
  //   Real fit (Primary/Secondary/Adjacent) + Expired + AutoDiscarded.
  //
  // URL ?fit=Primary,Secondary,Expired etc. Empty/absent = "All real
  // tiers" (the 3 score-based ones), excludes Expired+AutoDiscarded so
  // the main view stays clean — the user has to click those chips to see
  // them. Each item belongs to EXACTLY ONE tier via effectiveTier().
  const fitParam = searchParams.get('fit') || '';
  const fitTiers: Set<EvalTier> = new Set(
    fitParam
      .split(',')
      .map((s) => s.trim() as EvalTier)
      .filter((t): t is EvalTier => ALL_TIERS.includes(t)),
  );
  // "All" state: empty filter (= show the 3 real tiers). Excludes Expired
  // and AutoDiscarded so they don't pollute the main view by default.
  const allSelected = fitTiers.size === 0;
  // Toggle a tier on/off. Clicking "All" while no tiers are selected is a
  // no-op; clicking "All" while any tier IS selected resets to default.
  const toggleFit = (tier: EvalTier) => {
    const next = new Set(fitTiers);
    if (next.has(tier)) next.delete(tier);
    else next.add(tier);
    // Special case: if the user has explicitly selected exactly the 3
    // real tiers (which equals the default anyway), collapse to "All".
    if (next.size === 0) {
      setUrlParam('fit', null);
    } else if (
      next.size === REAL_FIT_TIERS.length &&
      REAL_FIT_TIERS.every((t) => next.has(t))
    ) {
      setUrlParam('fit', null);
    } else {
      setUrlParam('fit', ALL_TIERS.filter((t) => next.has(t)).join(','));
    }
  };
  const setAllFit = () => setUrlParam('fit', null);
  // Server now understands all 5 tiers (incl. Expired/AutoDiscarded) and does
  // the filtering in SQL. Empty = default (the 3 real tiers; pseudo-tiers
  // hidden until their chip is clicked). Otherwise send exactly the selected
  // tiers.
  const fitParamForServer = allSelected ? '' : ALL_TIERS.filter((t) => fitTiers.has(t)).join(',');

  // Evaluated-tab keyword search. URL-backed (`?eq=...`) so a refresh
  // / Back nav preserves the user's filter context, and so it composes
  // with the fit chips. Lower-case, debounce-free (only ~500 rows max,
  // client-side filter is instant).
  const evaluatedQ_ = searchParams.get('eq') || '';
  const setEvaluatedQuery = (v: string) => setUrlParam('eq', v || null);

  // "Remote only" toggle — URL-backed (`?remote=1`). Filters the visible
  // Evaluated list to rows whose work_mode is 'remote'. Client-side, so
  // it composes with the tier chips + keyword search.
  const remoteOnly = searchParams.get('remote') === '1';
  const setRemoteOnly = (v: boolean) => setUrlParam('remote', v ? '1' : null);

  // Debounce the keyword query so server-side search fires once the user
  // pauses typing — each change is a fresh paginated query now (search moved
  // server-side; the old client-side instant filter over the full set is gone
  // because we no longer ship the full set to the client).
  const [searchDebounced, setSearchDebounced] = useState(evaluatedQ_.trim());
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(evaluatedQ_.trim()), 300);
    return () => clearTimeout(t);
  }, [evaluatedQ_]);

  // Evaluated list — server-paginated + server-sorted + server-keyword-searched
  // (useInfiniteQuery). Each page is PAGE_SIZE rows; the tier filter, remote
  // toggle and keyword query are all SERVER params now, so the loaded pages are
  // already the filtered view (no client-side re-filtering). Tier-bucket counts
  // come from the server (stable full-set totals) so chips stay correct across
  // pages. This replaces the old load-everything-then-filter approach that
  // shipped a ~7.78 MB payload on open.
  const evaluatedQ = useInfiniteQuery({
    queryKey: queryKeys.matchesList({
      fit: fitParamForServer || 'all',
      sort: evaluatedSort,
      q: searchDebounced || undefined,
      remote: remoteOnly || undefined,
    }),
    queryFn: ({ pageParam }) =>
      apiGet<MatchesResponse>('/api/matches', {
        fit: fitParamForServer || undefined,
        sort: evaluatedSort,
        q: searchDebounced || undefined,
        remote: remoteOnly ? '1' : undefined,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return lastPage.items.length === PAGE_SIZE ? loaded : undefined;
    },
    // Auto-refresh: the laptop eval worker drains the backlog in the
    // background, so poll every 30s to surface new evaluations without a
    // manual reload. Keep showing the last data while refetching.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  });

  // Pending total for the tab badge (separate tiny query) — must pass the
  // same country filter so the badge count matches what's displayed.
  //
  // BUG FIX (2026-05-12): queryKey starts with 'matches-pending' (not
  // 'matches-pending-count') so React Query's prefix-match invalidation
  // catches BOTH this count query and the full pending list with the same
  // invalidateQueries({ queryKey: ['matches-pending'] }) call. Otherwise
  // the table refreshed on delete but the tab badge stayed stale.
  const pendingCountQ = useQuery({
    queryKey: ['matches-pending', 'count', country, searchTerm],
    queryFn: () => apiGet<PendingResponse>('/api/matches/pending', {
      limit: 1,
      country: country || undefined,
      keywords: searchTerm || undefined,
    }),
    staleTime: 15_000,
    // Keep the pending badge live as the worker clears the backlog.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  // Flattened, server-filtered pages. The server already applied tier/remote/
  // keyword filters, so the loaded pages ARE the view — no client re-filter.
  const evaluatedItems = useMemo<Match[]>(
    () => evaluatedQ.data?.pages.flatMap((p) => p.items) ?? [],
    [evaluatedQ.data],
  );
  const visibleEvaluated = evaluatedItems;
  const pendingTotal = pendingCountQ.data?.total ?? 0;

  // Tier-bucket counts from the server — the first page carries the full-set
  // 5-bucket totals (stable regardless of which chip/search is active).
  const serverCounts: MatchTierCounts | undefined = evaluatedQ.data?.pages?.[0]?.counts;
  const tierCounts = useMemo<Record<EvalTier, number>>(() => ({
    Primary: serverCounts?.Primary ?? 0,
    Secondary: serverCounts?.Secondary ?? 0,
    Adjacent: serverCounts?.Adjacent ?? 0,
    Expired: serverCounts?.Expired ?? 0,
    AutoDiscarded: serverCounts?.AutoDiscarded ?? 0,
  }), [serverCounts]);
  // Size of the CURRENT filtered set (tier+remote+keyword) — drives "N of M
  // shown" + the infinite-scroll stop. Distinct from the grand total below.
  const filteredTotal = evaluatedQ.data?.pages?.[0]?.total ?? 0;
  // Grand total across all 5 tiers (the "Evaluated (N)" header). Falls back to
  // the filtered total when counts are absent (legacy/rollback path).
  const evaluatedTotal = serverCounts
    ? tierCounts.Primary + tierCounts.Secondary + tierCounts.Adjacent + tierCounts.Expired + tierCounts.AutoDiscarded
    : filteredTotal;
  const selectedForCvCount = evaluatedItems.filter((m) => m.selected_for_cv).length;

  // Single-row CV-pool toggle. Optimistic — flips the cache so the
  // checkbox responds immediately, then PUTs the server. On error,
  // surface an alert and refetch to resync.
  const toggleCvMut = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      fetch('/api/cv-selections', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, on }),
      }).then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text().catch(() => '')}`);
        return r.json();
      }),
    onMutate: ({ id, on }) => {
      // Optimistic update across every matches cache (infinite + flat).
      qc.setQueriesData({ queryKey: queryKeys.matchesAll }, (old) =>
        flipSelectedInCache(old, (mid) => mid === id, on),
      );
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: queryKeys.matchesAll });
      alert(zh ? '更新收藏状态失败，请刷新页面查看当前状态。' : 'Failed to update CV selection — refresh to see current state.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cvSelections });
      // Refresh CVPicker's selected-set query so a just-saved row appears in
      // CV Writer.
      qc.invalidateQueries({ queryKey: queryKeys.matchesSelected() });
    },
  });

  // Bulk: mark/unmark every LOADED match for CV. With server pagination the
  // client only holds the loaded pages, so to avoid dropping selections that
  // live on un-loaded pages we start from the server's FULL current selected
  // set (cheap — it's the small CVPicker pool) and add/remove the loaded ids.
  // The PUT { selected: [...] } replaces the whole set; the server validates
  // each id with the report-filename regex.
  const bulkSetCv = async (on: boolean) => {
    const loadedIds = visibleEvaluated.map((m) => m.id);
    try {
      const current = await apiGet<MatchesResponse>('/api/matches', { selected: true });
      const targetIds = new Set(current.items.map((m) => m.id));
      for (const id of loadedIds) {
        if (on) targetIds.add(id);
        else targetIds.delete(id);
      }
      await fetch('/api/cv-selections', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected: Array.from(targetIds) }),
      }).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      });
      // Optimistically flip the loaded rows; next refetch reconciles.
      const loadedSet = new Set(loadedIds);
      qc.setQueriesData({ queryKey: queryKeys.matchesAll }, (old) =>
        flipSelectedInCache(old, (mid) => loadedSet.has(mid), on),
      );
      qc.invalidateQueries({ queryKey: queryKeys.cvSelections });
      qc.invalidateQueries({ queryKey: queryKeys.matchesSelected() });
    } catch (e) {
      alert(zh ? `批量更新收藏失败：${(e as Error).message}` : `Bulk CV update failed: ${(e as Error).message}`);
      qc.invalidateQueries({ queryKey: queryKeys.matchesAll });
    }
  };

  // Shared optimistic delete (handles the infinite + flat cache shapes).
  const deleteMatch = useDeleteMatch();

  // Infinite scroll: fetch the next page when the sentinel scrolls into view.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && evaluatedQ.hasNextPage && !evaluatedQ.isFetchingNextPage) {
          evaluatedQ.fetchNextPage();
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluatedQ.hasNextPage, evaluatedQ.isFetchingNextPage, visibleEvaluated.length]);

  const handleEvaluate = (rows: PendingRow[]) => {
    group.start(
      rows.map((r) => ({
        url: r.url,
        label: `${r.company || 'Unknown'} · ${r.title}`,
        // Forward to backend so LinkedIn/Indeed URLs use the scan's company/role
        // instead of falling back to "Unknown Company · Software Engineering Role".
        company: r.company || '',
        role: r.title || '',
      }))
    );
  };

  // Add a single role by pasting its JD link. Queues an evaluate job for the
  // URL (same backbone as Evaluate-all); the backend fetches the JD, scores
  // it, and writes a report that shows up on the Evaluated tab. The
  // EvaluationBanner tracks progress.
  const handleAddByUrl = () => {
    const url = addUrl.trim();
    if (!/^https?:\/\/\S+/i.test(url)) {
      alert(zh ? '请粘贴有效的职位链接（以 http:// 或 https:// 开头）。' : 'Paste a valid job URL (starting with http:// or https://).');
      return;
    }
    if (runningUrls.has(url)) {
      alert(zh ? '这个链接已经在评估队列里了。' : 'That URL is already in the evaluation queue.');
      return;
    }
    group.start([{ url, label: url }]);
    setAddUrl('');
  };

  const evaluateAll = async () => {
    const n = pendingTotal;
    if (n === 0) {
      alert(zh ? '没有待评估的岗位。' : 'No pending roles to evaluate.');
      return;
    }
    const hours = Math.ceil((n * 60) / 2 / 3600);
    const ok = confirm(zh
      ? `要把全部 ${n.toLocaleString()} 个待评估岗位加入队列吗？\n\n` +
        `系统会每次处理 2 个，预计总共需要约 ${hours} 小时。\n` +
        `可以直接关掉浏览器——任务会在后台继续跑。`
      : `Queue ALL ${n.toLocaleString()} pending roles for evaluation?\n\n` +
        `Ollama will process them 2 at a time. Estimated total: ~${hours} hour${hours === 1 ? '' : 's'}.\n` +
        `You can close the browser — jobs keep running in the background.`
    );
    if (!ok) return;
    try {
      const all = await apiGet<PendingResponse>('/api/matches/pending', {
        limit: 5000,
        country: country || undefined,
        keywords: searchTerm || undefined,
      });
      handleEvaluate(all.items);
      setTab('pending');
    } catch (e) {
      alert((zh ? '获取待评估列表失败：' : 'Failed to fetch pending list: ') + ((e as Error).message || (zh ? '未知错误' : 'unknown error')));
    }
  };

  // Two-click armed pattern for Clear all — consistent with per-row delete UX.
  const [clearArmed, setClearArmed] = useState(false);
  useEffect(() => {
    if (!clearArmed) return;
    const t = setTimeout(() => setClearArmed(false), 3000);
    return () => clearTimeout(t);
  }, [clearArmed]);

  const clearAllEvaluated = async () => {
    if (evaluatedTotal === 0) return;
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setClearArmed(false);
    try {
      const r = await fetch('/api/matches?confirm=true', {
        method: 'DELETE',
        headers: { 'X-Confirm-Destructive': 'yes' },
      });
      const j = await r.json();
      if (j.ok) {
        qc.invalidateQueries({ queryKey: ['matches'] });
        qc.invalidateQueries({ queryKey: ['matches-pending'] });
        qc.invalidateQueries({ queryKey: ['matches-pending-count'] });
        // Tailored PDFs are cleaned up server-side too — refresh CVPicker.
        qc.invalidateQueries({ queryKey: ['cv-tailored'] });
      } else {
        alert((zh ? '清空失败：' : 'Clear failed: ') + (j.error || (zh ? '未知错误' : 'unknown')));
      }
    } catch (e) {
      alert((zh ? '清空失败：' : 'Clear failed: ') + (e as Error).message);
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow={zh ? "职位匹配 · 筛选" : "Matches · triage"}
        title={zh ? "为你打开的每一扇门" : "Every door we opened for you."}
        sub={zh ? "待评估：扫描到但还没评分的职位。已评估：已按你的画像打过分。" : "Pending: scanned roles waiting for evaluation. Evaluated: already scored against your profile."}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => nav('/scanner')}>
              {zh ? "新建扫描" : "New scan"}
            </button>
            <button
              className="btn primary"
              onClick={evaluateAll}
              disabled={pendingTotal === 0}
              title={zh
                ? `把全部 ${pendingTotal.toLocaleString()} 个待评估岗位加入队列（已在跑的链接会跳过）`
                : `Queue all ${pendingTotal.toLocaleString()} pending roles (already-running URLs are skipped)`}
            >
              {zh ? "全部评估" : "Evaluate all"} ({pendingTotal.toLocaleString()})
            </button>
          </div>
        }
      />

      <LocalEvalStatusBanner />
      <EvaluationBanner group={group} />

      {/* Add a single role by JD link — paste a posting URL and evaluate it
          directly, without waiting for a scan to surface it. */}
      <div
        className="card"
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '10px 14px',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>
          {zh ? '按链接添加职位：' : 'Add a job by URL:'}
        </span>
        <input
          type="url"
          value={addUrl}
          onChange={(e) => setAddUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddByUrl(); }}
          placeholder={zh ? '粘贴职位 JD 链接（LinkedIn / Indeed / Greenhouse …）' : 'Paste a job URL (LinkedIn / Indeed / Greenhouse …)'}
          style={{
            flex: 1,
            minWidth: 240,
            padding: '7px 10px',
            border: '1px solid var(--line)',
            borderRadius: 6,
            fontSize: 13,
          }}
        />
        <button
          className="btn primary"
          onClick={handleAddByUrl}
          disabled={!addUrl.trim()}
          title={zh
            ? '抓取该链接的职位描述并按你的画像评分，结果会出现在「已评估」标签页'
            : "Fetch the JD at this URL and score it against your profile. The result appears in the Evaluated tab."}
        >
          {zh ? '添加并匹配' : 'Add and match'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--line)' }}>
        <button
          className="btn ghost"
          style={{
            border: 'none',
            borderRadius: 0,
            padding: '10px 16px',
            borderBottom: tab === 'pending' ? '2px solid var(--pri)' : '2px solid transparent',
            color: tab === 'pending' ? 'var(--ink)' : 'var(--ink3)',
            fontWeight: tab === 'pending' ? 600 : 400,
          }}
          onClick={() => setTab('pending')}
        >
          {zh ? "待评估" : "Pending"} ({pendingTotal.toLocaleString()})
        </button>
        <button
          className="btn ghost"
          style={{
            border: 'none',
            borderRadius: 0,
            padding: '10px 16px',
            borderBottom: tab === 'evaluated' ? '2px solid var(--pri)' : '2px solid transparent',
            color: tab === 'evaluated' ? 'var(--ink)' : 'var(--ink3)',
            fontWeight: tab === 'evaluated' ? 600 : 400,
          }}
          onClick={() => setTab('evaluated')}
        >
          {zh ? "已评估" : "Evaluated"} ({evaluatedTotal.toLocaleString()})
        </button>
      </div>

      {tab === 'pending' && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginBottom: 16,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              {SOURCE_CHOICES.map((s) => (
                <Chip
                  key={s.key || 'all'}
                  variant={pendingSource === s.key ? 'primary' : 'default'}
                  onClick={() => setPendingSource(s.key)}
                >
                  {s.label === 'All' ? (zh ? '全部' : 'All') : s.label}
                </Chip>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {POSTED_CHOICES.map((p) => (
                <Chip
                  key={p.label}
                  variant={pendingHours === p.hours ? 'primary' : 'default'}
                  onClick={() => setPendingHours(p.hours)}
                >
                  {!zh ? p.label
                    : p.label === 'All' ? '全部'
                    : p.label === '24h' ? '24 小时'
                    : p.label === '7d' ? '7 天'
                    : p.label === '30d' ? '30 天'
                    : p.label}
                </Chip>
              ))}
            </div>
            <input
              type="text"
              placeholder={zh ? '搜索公司或职位…' : 'Search company or role…'}
              value={pendingQ}
              onChange={(e) => setPendingQ(e.target.value)}
              style={{
                flex: 1,
                minWidth: 200,
                padding: '6px 12px',
                border: '1px solid var(--line)',
                borderRadius: 6,
                fontSize: 13,
                background: 'var(--surface)',
                color: 'var(--ink)',
              }}
            />
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--ink2)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={pendingWithSalary}
                onChange={(e) => setPendingWithSalary(e.target.checked)}
                style={{ accentColor: 'var(--pri)' }}
              />
              {zh ? '仅显示有薪资' : 'Only with salary'}
            </label>
            {/* Sort dropdown — Pending tab. URL-backed; backend sorts before
                pagination so cross-page order is consistent. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink2)' }}>
              {zh ? '排序' : 'Sort'}
              <select
                value={pendingSort}
                onChange={(e) => setSort(e.target.value)}
                style={{
                  padding: '4px 8px',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontSize: 12,
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  cursor: 'pointer',
                }}
              >
                <option value="first_seen">{zh ? '最新优先' : 'Newest first'}</option>
                <option value="salary">{zh ? '薪资高→低' : 'Salary high → low'}</option>
                <option value="company">{zh ? '公司 A→Z' : 'Company A → Z'}</option>
                <option value="title">{zh ? '职位 A→Z' : 'Title A → Z'}</option>
              </select>
            </label>
          </div>

          <PendingJobsTable
            filters={pendingFilters}
            runningUrls={runningUrls}
            onEvaluate={handleEvaluate}
            refreshKey={group.doneCount + group.failedCount}
          />
        </>
      )}

      {tab === 'evaluated' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              variant={allSelected ? 'primary' : 'default'}
              onClick={setAllFit}
              title={zh ? '显示全部已评估岗位' : 'Show every evaluated role'}
            >
              {zh ? '全部' : 'All'} ({evaluatedTotal.toLocaleString()})
            </Chip>
            {ALL_TIERS.map((tier) => {
              const meta = fitTierLabels(zh)[tier];
              // Server-provided 5-bucket counts (stable full-set totals). In the
              // legacy/rollback path the two pseudo-tiers come back 0 and their
              // chips are hidden below.
              const count = tierCounts[tier];
              // Hide pseudo-tier chips when empty (no expired/auto-discarded
              // items to filter on). Real fit chips always render so the
              // user sees the 3 main buckets even if some are zero.
              const isPseudoTier = tier === 'Expired' || tier === 'AutoDiscarded';
              if (isPseudoTier && count === 0) return null;
              const isOn = fitTiers.has(tier);
              return (
                <Chip
                  key={tier}
                  variant={isOn ? 'primary' : 'default'}
                  onClick={() => toggleFit(tier)}
                  title={zh
                    ? `${meta.description} 点击${isOn ? '取消筛选' : '只看这一类'}。`
                    : `${meta.description} Click to ${isOn ? 'remove from' : 'isolate to'} this category.`}
                >
                  {isOn && '✓ '}{meta.label} <span style={{ opacity: 0.65, fontWeight: 400 }}>· {meta.subtitle}</span> ({count.toLocaleString()})
                </Chip>
              );
            })}
            {/* Sort dropdown — Evaluated tab. Default = score desc (matches
                pre-sort behaviour). Reputation falls to the bottom when
                missing so old reports without Block H don't pollute the
                top of the list. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink2)', marginLeft: 4 }}>
              {zh ? '排序' : 'Sort'}
              <select
                value={evaluatedSort}
                onChange={(e) => setSort(e.target.value)}
                style={{
                  padding: '4px 8px',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontSize: 12,
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  cursor: 'pointer',
                }}
              >
                <option value="score">{zh ? '按匹配分（默认）' : 'Score (default)'}</option>
                <option value="salary">{zh ? '薪资 高→低' : 'Salary high → low'}</option>
                <option value="reputation">{zh ? '公司声誉' : 'Company reputation'}</option>
                <option value="posted">{zh ? '最新优先' : 'Newest first'}</option>
                <option value="company">{zh ? '公司 A→Z' : 'Company A → Z'}</option>
              </select>
            </label>
            <div
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}
            >
              {/* Expired + Auto-discarded checkboxes removed — they're now
                  first-class tier chips in the row above (rendered by the
                  ALL_TIERS.map loop). Clicking the Expired or Auto-discarded
                  chip isolates that category just like clicking Strong fit. */}
              <button
                className="btn"
                onClick={clearAllEvaluated}
                disabled={evaluatedTotal === 0}
                style={{
                  fontSize: 11,
                  color: clearArmed ? '#fff' : '#b43a3a',
                  background: clearArmed ? '#b43a3a' : 'transparent',
                  border: clearArmed ? '1px solid #b43a3a' : undefined,
                  transition: 'all 120ms',
                }}
                title={clearArmed
                  ? (zh ? '再点一次会永久删除所有评估报告' : 'Click again to permanently delete all reports')
                  : (zh
                    ? '删除所有评估报告、对应的记录行，以及已定制的简历 PDF。「我的申请」和待评估列表不受影响。'
                    : 'Delete every reports/NNN-*.md, its tracker TSV row, and any tailored CV PDFs. applications.md and pending rows are NOT touched.')}
              >
                {clearArmed
                  ? (zh ? '确定？' : 'Sure?')
                  : (zh ? `清空全部 (${evaluatedTotal.toLocaleString()})` : `Clear all (${evaluatedTotal.toLocaleString()})`)}
              </button>
            </div>
          </div>
          {/* Search + bulk-CV-select row. Search filters the visible
              list client-side; bulk buttons operate on the visible
              (post-search) subset so the user can e.g. search "anthropic"
              then click "Mark all visible" to pool every Anthropic role. */}
          {evaluatedTotal > 0 && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={evaluatedQ_}
                onChange={(e) => setEvaluatedQuery(e.target.value)}
                placeholder={zh ? '搜索公司、职位或摘要…' : 'Search company, role, or summary…'}
                style={{
                  flex: 1,
                  minWidth: 240,
                  padding: '8px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  background: '#fff',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
              {evaluatedQ_ && (
                <button className="btn" onClick={() => setEvaluatedQuery('')} style={{ fontSize: 12 }}>
                  {zh ? '清除' : 'Clear'}
                </button>
              )}
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink2)', whiteSpace: 'nowrap', cursor: 'pointer' }}
                title={zh
                  ? '只看远程岗位。尽力而为：职位名称里写明 Remote/Hybrid/On-site 时以它为准，否则用 LLM 给的地点分。注意地点分不一定分得清「完全远程」和「就在你想去的城市」，所以可能混进少量现场岗位。'
                  : "Show only roles classified as remote. Best-effort: uses the job title's own wording when it says Remote/Hybrid/On-site, otherwise the LLM's Location score. Note the Location score can't always tell full-remote apart from your preferred city, so a few on-site roles may slip in."}
              >
                <input
                  type="checkbox"
                  checked={remoteOnly}
                  onChange={(e) => setRemoteOnly(e.target.checked)}
                />
                {zh ? '只看远程' : 'Remote only'}
              </label>
              <span style={{ fontSize: 11.5, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>
                {evaluatedQ_ ? (zh
                  ? `显示 ${visibleEvaluated.length.toLocaleString()} / ${filteredTotal.toLocaleString()} · `
                  : `${visibleEvaluated.length.toLocaleString()} of ${filteredTotal.toLocaleString()} shown · `) : ''}
                {zh ? `已收藏 ${selectedForCvCount} 个（当前已加载）` : `${selectedForCvCount} saved for CV (loaded)`}
              </span>
              {/* Bulk buttons. Label shows the EXACT count this click
                  will affect so the user never wonders "is this
                  everything, or only what I filtered?" The count tracks
                  the search input — no filter shows the full total,
                  active filter shows the filtered subset, and the
                  word "filtered" / "all" makes the scope explicit. */}
              {(() => {
                const willAdd = visibleEvaluated.filter((m) => !m.selected_for_cv).length;
                const willRemove = visibleEvaluated.filter((m) => m.selected_for_cv).length;
                // With server pagination, bulk ops act on the LOADED rows. Scroll
                // to load more, then bulk again, to cover a large filtered set.
                const scopeWord = zh ? '已加载' : 'loaded';
                return (
                  <>
                    <button
                      className="btn"
                      onClick={() => bulkSetCv(true)}
                      disabled={willAdd === 0}
                      style={{ fontSize: 11.5 }}
                      title={zh
                        ? `把 ${willAdd} 个${scopeWord}岗位（当前可见且尚未收藏的）加入「简历定制」`
                        : `Save ${willAdd} ${scopeWord} match${willAdd === 1 ? '' : 'es'} (every visible one not already saved) for CV tailoring on /cv`}
                    >
                      {zh ? `收藏${scopeWord} ${willAdd} 个` : `Save ${scopeWord} ${willAdd} for CV`}
                    </button>
                    <button
                      className="btn ghost"
                      onClick={() => bulkSetCv(false)}
                      disabled={willRemove === 0}
                      style={{ fontSize: 11.5 }}
                      title={zh
                        ? `把 ${willRemove} 个${scopeWord}岗位移出「简历定制」`
                        : `Unsave ${willRemove} ${scopeWord} match${willRemove === 1 ? '' : 'es'} currently in the CV pool`}
                    >
                      {zh ? `取消收藏${scopeWord} ${willRemove} 个` : `Unsave ${scopeWord} ${willRemove}`}
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {/* Score-meaning legend — explains the Score column once so
              users don't have to wonder what "60" or "30" means. Hidden
              when there are no evaluated matches yet (the empty-state
              message inside the card already handles that case). */}
          {evaluatedTotal > 0 && (
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--ink3)',
                marginBottom: 14,
                lineHeight: 1.55,
                maxWidth: 760,
              }}
            >
              {zh ? (
                <>
                  每一行的数字是匹配分（0–100），综合技术能力、岗位方向、薪资、地点、成长空间、工作节奏六个维度，
                  衡量你的简历和这个岗位有多合。如果岗位层级远高于你现在的水平，或者方向明显不对，分数会被压低。
                  在任意一行点 <em>收藏做简历</em>，这个岗位就会进入「简历定制」，投递前可以在那里生成一份专门针对它的简历。
                </>
              ) : (
                <>
                  The number on each row is your fit score (0–100). It reflects
                  how well your CV matches the role across 6 dimensions —
                  technical skills, mission, comp, location, growth, pace.
                  Roles get capped down when the seniority is far above your
                  level or the domain is off. Click <em>Save for CV</em> on any
                  row to send it to the CV page, where you can generate a
                  custom-tailored CV per role before applying.
                </>
              )}
            </div>
          )}
          <div className="card">
            {evaluatedQ.isLoading && <div className="empty">{zh ? '加载中…' : 'Loading matches…'}</div>}
            {evaluatedQ.isError && (
              <div className="empty">{zh ? '加载失败，请稍后重试。' : "Couldn't load matches. Is server.js running?"}</div>
            )}
            {evaluatedQ.data && evaluatedItems.length === 0 && (
              <div className="empty">
                <span className="serif">{zh ? '还没有评估过的岗位。' : 'No evaluated matches yet.'}</span>
                {zh
                  ? '去「职位板」挑几个岗位点「评估这个岗位」，评完就会出现在这里。'
                  : 'Switch to Pending and evaluate a few roles to populate this list.'}
              </div>
            )}
            {evaluatedQ.data && evaluatedItems.length > 0 && visibleEvaluated.length === 0 && (
              <div className="empty">
                <span className="serif">{zh ? "当前筛选下没有匹配。" : "No matches in this view."}</span>
                {zh ? "试试放宽分档、排序或搜索条件。" : "Try widening the fit chips, the sort, or the search query."}
              </div>
            )}
            {visibleEvaluated.map((m) => (
              <MatchRow
                key={m.id}
                match={m}
                onOpen={(mm) => nav(`/job/${mm.id}`)}
                onToggleCv={(mm, next) => toggleCvMut.mutate({ id: mm.id, on: next })}
                onMarkExpired={async (mm) => {
                  // Manual expired-mark for Indeed/Glassdoor entries the
                  // auto-sweep can't probe (CF blocks VM IP). Server cascades
                  // blocklist + cv-selections + DB liveness. Refresh the matches
                  // query — the row then disappears (default Hide expired) or
                  // shows a red chip.
                  try {
                    const r = await fetch(`/api/liveness/mark/${encodeURIComponent(mm.id)}`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ state: 'expired' }),
                    });
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    qc.invalidateQueries({ queryKey: queryKeys.matchesAll });
                  } catch (e) {
                    alert((zh ? '标记过期失败：' : 'Mark expired failed: ') + (e as Error).message);
                  }
                }}
                // Two-click confirm is handled inside MatchRow; the shared hook
                // does the optimistic cache removal + cascade invalidations.
                onDelete={(mm) => deleteMatch.mutate(mm.id)}
              />
            ))}
            {/* Infinite-scroll sentinel + manual fallback. The IntersectionObserver
                (see useEffect) calls fetchNextPage when this scrolls into view. */}
            {visibleEvaluated.length > 0 && evaluatedQ.hasNextPage && (
              <div
                ref={loadMoreRef}
                style={{ padding: '14px 0', textAlign: 'center' }}
              >
                <button
                  className="btn ghost"
                  onClick={() => evaluatedQ.fetchNextPage()}
                  disabled={evaluatedQ.isFetchingNextPage}
                  style={{ fontSize: 12 }}
                >
                  {evaluatedQ.isFetchingNextPage
                    ? (zh ? '加载中…' : 'Loading…')
                    : (zh
                      ? `加载更多（已显示 ${visibleEvaluated.length.toLocaleString()} / ${filteredTotal.toLocaleString()}）`
                      : `Load more (${visibleEvaluated.length.toLocaleString()} of ${filteredTotal.toLocaleString()})`)}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
